import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import {
  ErrorMessage,
  Form,
  withFormik,
} from 'formik';

import { Form as SemanticForm } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { InputFormField } from '@playbookux/playbook-web-core';

import withGa from '../with-ga';
import { config } from '../../../config';

import {
  BottomContainer,
  Button,
  Checkbox,
  Container,
  Divider,
  Error,
  ErrorSubmit,
  Field,
  ForgotPassword,
  HaveAccount,
  LinkedInIcon,
  Logo,
  PasswordOptionsContainer,
  PrivacyPolicy,
  PrivacyPolicyContainer,
  RecaptchaContainer,
  Segment,
  SignInSSO,
  SSOIcons,
  Title,
} from './styles';

import {
  setLinkedInState,
  signInWithPassword,
  signOut,
} from '../../actions';
import schema from './schema';

const state = Math.random()
  .toString(36)
  .slice(2);

const SignIn = ({
  setLinkedInState: updateLinkedInState, status, handleSubmit: onSubmit, setFieldValue,
}) => {
  const isRecaptchaEnabled = config.REACT_APP_RECAPTCHA_ENABLED === 'true';
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uri, setUri] = useState(null);
  const recaptchaRef = useRef();
  const recaptchaRefVisible = useRef();

  useEffect(() => {
    const newUri = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${
      config.REACT_APP_LINKEDIN_CLIENT_ID
    }&redirect_uri=${config.REACT_APP_LINKEDIN_REDIRECT_URI}&state=${state}&scope=${
      config.REACT_APP_LINKEDIN_SCOPE
    }`;

    setUri(newUri);
    updateLinkedInState(state);
  }, []);

  const isCompany = config.REACT_APP_PLATFORM_TYPE === 'company';
  const title = isCompany ? 'Company Sign In' : 'Admin Sign In';
  const error = status && status.error;
  const isLoading = status && status.isLoading;

  useEffect(() => {
    if (error && recaptchaRefVisible?.current) {
      recaptchaRefVisible.current.reset();
    }
  }, [error]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isRecaptchaEnabled) {
      setIsButtonDisabled(true);
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 1000);
    }
    await recaptchaRef?.current?.execute();
    await onSubmit();
  };

  return (
    <Container>
      <Logo src="/images/playbook-logo.png" alt="Playbook" />

      <Segment>
        <Title>
          {title}
        </Title>

        <Form autoComplete="off">
          <SemanticForm as="div">
            <Field
              component={InputFormField}
              labelFontSize="14px"
              labelText="Email address"
              name="email"
            />
            <ErrorMessage component={Error} name="email" />

            <Field
              component={InputFormField}
              labelFontSize="14px"
              labelText="Password"
              name="password"
              type={!showPassword ? 'password' : 'text'}
              autoComplete="new-password"
              icon={{
                link: true,
                name: !showPassword ? 'eye' : 'eye slash',
                onClick: () => setShowPassword(!showPassword),
              }}
            />
            <ErrorMessage component={Error} name="password" />

            <PasswordOptionsContainer>
              <Checkbox
                label="Keep me signed in"
                name="keepSignedIn"
              />

              <ForgotPassword to="/forgot-password">Forgot your password?</ForgotPassword>
            </PasswordOptionsContainer>

            <BottomContainer>
              {isRecaptchaEnabled && (
                error ? (
                  <RecaptchaContainer>
                    <ReCAPTCHA
                      ref={recaptchaRefVisible}
                      sitekey={config.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_V2}
                      onChange={async (value) => {
                        setFieldValue('recaptcha', { recaptchaToken: value, recaptchaVersion: 'v2' });
                        await recaptchaRef?.current?.execute();
                        await onSubmit();
                      }}
                    />
                  </RecaptchaContainer>
                ) : (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={config.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                    onChange={(value) => {
                      setFieldValue('recaptcha', { recaptchaToken: value, recaptchaVersion: 'v3' });
                    }}
                    size="invisible"
                  />
                )
              )}
              <Button
                loading={isLoading}
                onClick={handleSubmit}
                type="submit"
                disabled={isButtonDisabled}
              >
                Sign In
              </Button>

              {error && !error?.includes('captcha') && <ErrorSubmit>{error}</ErrorSubmit>}

              <Divider />

              <Button href={encodeURI(uri)} disabled={isLoading}>
                <LinkedInIcon />
                Sign In with Linkedin
              </Button>
            </BottomContainer>
          </SemanticForm>
        </Form>
      </Segment>

      {isCompany && (
        <HaveAccount>
          {"Don't have an account? "}
          <Link to="/sign-up">Sign up</Link>
        </HaveAccount>
      )}

      {isCompany && (
        <>
          <SignInSSO to="/sign-in-sso">
            Sign in with SSO (Google, Azure, Okta, ADFS and more)
          </SignInSSO>
          <SSOIcons src="/images/sso-icons.png" />
        </>
      )}

      <PrivacyPolicyContainer>
        <PrivacyPolicy>
          By using Playbook UX LLC you agree to the
          {' '}
          <a target="_blank" rel="noopener noreferrer" href="https://www.playbookux.com/terms-of-service-clients/">Terms & Conditions</a>
          {' '}
          and
          {' '}
          <a target="_blank" rel="noopener noreferrer" href="https://www.playbookux.com/privacy-policy-company/">Privacy Policy</a>
        </PrivacyPolicy>
      </PrivacyPolicyContainer>
    </Container>
  );
};

SignIn.propTypes = {
  status: PropTypes.shape({
    error: PropTypes.string,
    isLoading: PropTypes.bool,
  }),
  handleSubmit: PropTypes.func.isRequired,
  setLinkedInState: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

SignIn.defaultProps = {
  status: {},
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setLinkedInState,
  signInWithPassword,
  signOut,
}, dispatch);

export default compose(
  connect(null, mapDispatchToProps),
  withFormik({
    enableReinitialize: true,
    validationSchema: schema,
    mapPropsToValues: () => ({
      email: '',
      password: '',
      recaptcha: { recaptchaToken: '', recaptchaVersion: '' },
    }),
    handleSubmit: async (values, prop) => {
      const {
        props,
        setStatus,
      } = prop;
      setStatus({
        error: null,
        isLoading: true,
      });

      try {
        const { email, password, recaptcha } = values;
        const { admin } = await props.signInWithPassword(email, password, recaptcha);

        if (config.REACT_APP_PLATFORM_TYPE === 'admin' && !admin) {
          props.signOut();
        }
      } catch (err) {
        const error = (err && err.code && err.message)
          ? err.message
          : 'Error while signing in';

        setStatus({
          error,
          isLoading: false,
        });
      }
    },
  }),
  withGa,
)(SignIn);
